import { CustomLayerInterface, CustomRenderMethodInput, Map as MlMap, MercatorCoordinate } from "maplibre-gl";
import { OBSTACLE_ICON_SIZE, drawObstacleIcon } from "./webgl-maplibre.obstacle-symbols";

/**
 * Custom MapLibre layer that renders obstacles at their true 3D altitude,
 * bypassing the terrain-drape that ordinary `circle` / `line` layers receive
 * when terrain is enabled. Cable chains become straight 3D segments between
 * endpoint elevations (cables hang over valleys); point obstacles render as
 * the 2D obstacle icon at `dem_elev + 25 m`.
 *
 * No pass is depth-tested against the terrain: our DEM samples never match
 * MapLibre's smoothed terrain mesh, so a tower in a valley would be hidden,
 * and its 2D icon is suppressed.
 *
 * Line geometry is expanded into screen-space triangle quads in the vertex
 * shader — `gl.lineWidth > 1` is a documented no-op on WebGL2/ANGLE (Android
 * Chromium clamps to 1), so `gl.LINES` would render every cable as a 1 px
 * hairline on the target device regardless of the value passed.
 */

const LINE_WIDTH_PX = 4.0;
// Same on-screen size as the 2D `vm-obstacle-symbols` icon: 64 px at pixel
// ratio 2, times its `icon-size`.
const POINT_SIZE_PX = 32 * OBSTACLE_ICON_SIZE;

// Translucent vertical "curtain" rendered beneath each cable segment, in the
// cable's colour at this opacity. The top edge follows the cable; the bottom
// edge follows the terrain elevation densely sampled along the span (setData's
// `ground` arg — built in refreshObstacles3d) so the sheet drops into valleys
// and meets the ground. Drawn with the depth test OFF (see render() pass 2) so
// its ground seam can't z-fight the terrain mesh.
const WALL_ALPHA = 0.22;

// Line shader: each input segment provides 6 vertices (two triangles forming
// a quad). Per-vertex attributes carry both endpoints + a side bit (-1 / +1);
// the vertex shader extrudes the current endpoint perpendicular to the
// segment's screen-space direction by `LINE_WIDTH_PX/2` pixels.
//
// Near-plane guard: at MAX_PITCH 85° a cable endpoint can sit at or behind
// the camera near-plane, where `cur.w <= 0` makes `cur.xy / cur.w` explode or
// sign-flip and the extruded quad spans the whole viewport as red noise.
// Push such vertices outside clip space so MapLibre's clipper drops the tri.
// `u_terrain_exaggeration` scales the mercator z component so 3D obstacles
// sit at the same rendered height as the exaggerated terrain underneath.
// Vertex z is uploaded unscaled — exaggeration is applied per frame so a
// future runtime-tunable exaggeration doesn't require a refresh to resync.
const LINE_VERT_SRC = `
attribute vec3 a_pos_cur;
attribute vec3 a_pos_oth;
attribute float a_side;
attribute vec4 a_color;
uniform mat4 u_matrix;
uniform vec2 u_viewport;
uniform float u_half_width;
uniform float u_terrain_exaggeration;
varying float v_dist_w;
varying float v_w;
varying float v_half_width;
varying vec4 v_color;
void main() {
  v_color = a_color;
  vec3 cur_pos = vec3(a_pos_cur.xy, a_pos_cur.z * u_terrain_exaggeration);
  vec3 oth_pos = vec3(a_pos_oth.xy, a_pos_oth.z * u_terrain_exaggeration);
  vec4 cur = u_matrix * vec4(cur_pos, 1.0);
  vec4 oth = u_matrix * vec4(oth_pos, 1.0);
  // Clip a segment crossing the camera plane before dividing by w. Rejecting
  // either endpoint discarded the visible approach to a destination whenever
  // the departure passed behind the camera.
  const float minW = 0.00001;
  if (cur.w <= minW && oth.w <= minW) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  if (cur.w < minW) {
    cur = mix(cur, oth, (minW - cur.w) / (oth.w - cur.w));
    cur.w = minW;
  } else if (oth.w < minW) {
    oth = mix(oth, cur, (minW - oth.w) / (cur.w - oth.w));
    oth.w = minW;
  }
  vec2 curNdc = cur.xy / cur.w;
  vec2 othNdc = oth.xy / oth.w;
  vec2 d = othNdc - curNdc;
  float len = max(length(d * u_viewport), 1e-3);
  // |a_side| is 1 at the segment start and 2 at its end. The end measures
  // toward the start, so it flips dir back: both ends then share one normal,
  // and the sign of a_side is the same side of the line at both ends.
  float endSign = abs(a_side) > 1.5 ? -1.0 : 1.0;
  vec2 dir = endSign * (d * u_viewport) / len;
  vec2 nrm = vec2(-dir.y, dir.x);
  // One extra pixel on each side holds the smoothed edge (see LINE_FRAG_SRC).
  // WebGL 1 interpolates varyings with perspective correction. The distance
  // must be linear on screen, so it goes out times w and comes back divided
  // by the interpolated w. Plain v_dist cut teeth into cables that run away
  // from the camera, along each quad diagonal.
  float dist = sign(a_side) * (u_half_width + 1.0);
  v_half_width = u_half_width;
  v_dist_w = dist * cur.w;
  v_w = cur.w;
  vec2 offsetPx = nrm * dist;
  vec2 offsetNdc = (offsetPx / u_viewport) * 2.0;
  gl_Position = vec4(cur.xy + offsetNdc * cur.w, cur.z, cur.w);
}
`;

// Point sprite with the obstacle icon. `a_lighted` (0 / 1) picks the half of
// the 2-icon atlas. The sprite centre moves up by half its size, so the icon
// bottom sits on the obstacle position, like `icon-anchor: bottom` in 2D.
const POINT_VERT_SRC = `
attribute vec3 a_mercator;
attribute float a_lighted;
uniform mat4 u_matrix;
uniform float u_point_size;
uniform vec2 u_viewport;
uniform float u_terrain_exaggeration;
varying float v_lighted;
void main() {
  v_lighted = a_lighted;
  vec3 pos = vec3(a_mercator.xy, a_mercator.z * u_terrain_exaggeration);
  vec4 p = u_matrix * vec4(pos, 1.0);
  if (p.w <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    return;
  }
  p.y += (u_point_size / u_viewport.y) * p.w;
  gl_Position = p;
  gl_PointSize = u_point_size;
}
`;

// Discard instead of blend: the pass sets no blend state, so a transparent
// texel must not reach the canvas.
const POINT_FRAG_SRC = `
precision mediump float;
uniform sampler2D u_icons;
varying float v_lighted;
void main() {
  vec4 c = texture2D(u_icons, vec2((gl_PointCoord.x + v_lighted) * 0.5, gl_PointCoord.y));
  if (c.a < 0.5) discard;
  gl_FragColor = vec4(c.rgb, 1.0);
}
`;

/** 128 x 64 atlas: standard icon left, lighted icon right. */
function buildIconAtlas(): ImageData {
  const atlas = new ImageData(128, 64);
  for (const lighted of [false, true]) {
    const icon = drawObstacleIcon(lighted).data;
    for (let y = 0; y < 64; y++) {
      atlas.data.set(icon.subarray(y * 64 * 4, (y + 1) * 64 * 4), (y * 128 + (lighted ? 64 : 0)) * 4);
    }
  }
  return atlas;
}

// Smoothed edge: the canvas has no MSAA (MapLibre's default is antialias:
// false), so a hard-edged quad shows a pixel staircase on diagonals. Coverage
// falls from 1 to 0 across the outer pixel. Output is premultiplied alpha.
// The half width arrives as a varying: a uniform shared by both stages must
// match precision, and this stage may fall back to mediump. highp where the
// device has it, because w can be large and the distance is v_dist_w / v_w.
const LINE_FRAG_SRC = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying float v_dist_w;
varying float v_w;
varying float v_half_width;
varying vec4 v_color;
void main() {
  float a = clamp(v_half_width + 0.5 - abs(v_dist_w / v_w), 0.0, 1.0) * v_color.a;
  gl_FragColor = vec4(v_color.rgb * a, a);
}
`;

// Round joins and caps: one smoothed disc per cable vertex, the cable's width
// across. A quad per segment has square ends, so every bend in a multi-tower
// cable left a wedge-shaped gap on its outer side.
const JOIN_VERT_SRC = `
attribute vec3 a_pos;
attribute vec4 a_color;
uniform mat4 u_matrix;
uniform float u_terrain_exaggeration;
uniform float u_half_width;
varying vec4 v_color;
varying float v_half_width;
void main() {
  v_color = a_color;
  v_half_width = u_half_width;
  vec4 c = u_matrix * vec4(a_pos.xy, a_pos.z * u_terrain_exaggeration, 1.0);
  if (c.w <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 0.0;
    return;
  }
  gl_Position = c;
  gl_PointSize = 2.0 * (u_half_width + 1.0);
}
`;

// Same coverage rule as LINE_FRAG_SRC, over the distance from the disc centre.
const JOIN_FRAG_SRC = `
precision mediump float;
varying vec4 v_color;
varying float v_half_width;
void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0 * (v_half_width + 1.0);
  float a = clamp(v_half_width + 0.5 - d, 0.0, 1.0) * v_color.a;
  gl_FragColor = vec4(v_color.rgb * a, a);
}
`;

/**
 * One join vertex (x, y, z, rgba) per distinct cable vertex: the start of
 * every segment, plus its end when the next segment does not start there.
 */
function lineJoins(lines: Float32Array, colors: Float32Array): Float32Array {
  const segCount = (lines.length / 3) >> 1;
  const out: number[] = [];
  for (let s = 0; s < segCount; s++) {
    const a = s * 6;
    const rgba = colors.subarray(s * 4, s * 4 + 4);
    out.push(lines[a], lines[a + 1], lines[a + 2], ...rgba);
    const n = a + 6;
    const continues = s + 1 < segCount && lines[n] === lines[a + 3] && lines[n + 1] === lines[a + 4];
    if (!continues) out.push(lines[a + 3], lines[a + 4], lines[a + 5], ...rgba);
  }
  return new Float32Array(out);
}

// Coloured-triangle program for cable curtains and for the ready triangle list
// that `setData` takes as `cones`: a plain 3D transform with PER-VERTEX colour
// (a_color), so each curtain keeps its cable's colour. No webcam cone is drawn
// any more: webcams are hidden in 3D. The approach-sector layer passes its
// sector fills in that list. The bottom vertices of a curtain carry the ground
// height, or z = 0 when `setData` got no ground data.
const CONE_VERT_SRC = `
attribute vec3 a_pos;
attribute vec4 a_color;
uniform mat4 u_matrix;
uniform float u_terrain_exaggeration;
varying vec4 v_color;
void main() {
  v_color = a_color;
  vec3 p = vec3(a_pos.xy, a_pos.z * u_terrain_exaggeration);
  vec4 c = u_matrix * vec4(p, 1.0);
  if (c.w <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  gl_Position = c;
}
`;

const CONE_FRAG_SRC = `
precision mediump float;
varying vec4 v_color;
void main() {
  gl_FragColor = v_color;
}
`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error("createShader failed");
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`obstacle-3d shader compile failed: ${log}`);
  }
  return sh;
}

function link(gl: WebGLRenderingContext, vs: WebGLShader, fs: WebGLShader): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error("createProgram failed");
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`obstacle-3d link failed: ${log}`);
  }
  return program;
}

/**
 * Expand input LINES vertex stream (pairs of consecutive xyz triplets, one
 * per segment) into triangle quads with 6 vertices per segment. Each output
 * vertex carries (curMerc[3], othMerc[3], side[1]) = 7 floats.
 *
 * Per segment A→B emit: (A,B,-1),(A,B,+1),(B,A,-2),(A,B,+1),(B,A,+2),(B,A,-2).
 * The side is ±1 at A and ±2 at B; see LINE_VERT_SRC.
 * Each vertex also carries the segment's RGBA from `colors` (4 per segment).
 * Two triangles forming a screen-aligned quad straddling the segment.
 */
const LINE_STRIDE = 11; // cur xyz, other xyz, side, rgba

function expandLinesToQuads(lines: Float32Array, colors: Float32Array): Float32Array {
  const segCount = (lines.length / 3) >> 1; // pairs of xyz
  const out = new Float32Array(segCount * 6 * LINE_STRIDE);
  let o = 0;
  for (let s = 0; s < segCount; s++) {
    const a = s * 6;
    const ax = lines[a],
      ay = lines[a + 1],
      az = lines[a + 2];
    const bx = lines[a + 3],
      by = lines[a + 4],
      bz = lines[a + 5];
    const push = (cx: number, cy: number, cz: number, ox: number, oy: number, oz: number, side: number) => {
      out[o++] = cx;
      out[o++] = cy;
      out[o++] = cz;
      out[o++] = ox;
      out[o++] = oy;
      out[o++] = oz;
      out[o++] = side;
      out.set(colors.subarray(s * 4, s * 4 + 4), o);
      o += 4;
    };
    push(ax, ay, az, bx, by, bz, -1);
    push(ax, ay, az, bx, by, bz, +1);
    push(bx, by, bz, ax, ay, az, -2);
    push(ax, ay, az, bx, by, bz, +1);
    push(bx, by, bz, ax, ay, az, +2);
    push(bx, by, bz, ax, ay, az, -2);
  }
  return out;
}

/**
 * Expand the LINES vertex stream into vertical wall quads — one translucent
 * curtain per cable segment. 6 vertices per segment, 7 floats each (x, y, z,
 * then the cable's rgb at WALL_ALPHA; true 3D positions, not screen-extruded).
 *
 * The top edge follows the cable endpoints; the bottom edge follows the terrain
 * elevation at those same endpoints, supplied in `ground` (identical layout to
 * `lines`, but z = bare terrain height). Pinning the bottom to the ground means
 * the sheet no longer plunges through the terrain surface down to sea level, so
 * it stops z-fighting terrain along that crossing. If `ground` is absent or
 * length-mismatched, the bottom falls back to mercator z = 0.
 *
 * Per segment A→B emit two triangles: (A_top,B_top,B_grd),(A_top,B_grd,A_grd).
 * The quad is a true vertical plane through the two endpoints, so it stays
 * planar. Culling is off for this pass so the curtain is visible from both sides.
 */
function expandLinesToWalls(lines: Float32Array, ground: Float32Array, colors: Float32Array): Float32Array {
  const segCount = (lines.length / 3) >> 1;
  const out = new Float32Array(segCount * 6 * 7);
  const haveGround = ground.length === lines.length;
  let o = 0;
  for (let s = 0; s < segCount; s++) {
    const a = s * 6;
    const ax = lines[a],
      ay = lines[a + 1],
      az = lines[a + 2];
    const bx = lines[a + 3],
      by = lines[a + 4],
      bz = lines[a + 5];
    const agz = haveGround ? ground[a + 2] : 0;
    const bgz = haveGround ? ground[a + 5] : 0;
    const push = (x: number, y: number, z: number) => {
      out[o++] = x;
      out[o++] = y;
      out[o++] = z;
      out[o++] = colors[s * 4];
      out[o++] = colors[s * 4 + 1];
      out[o++] = colors[s * 4 + 2];
      out[o++] = WALL_ALPHA;
    };
    push(ax, ay, az);
    push(bx, by, bz);
    push(bx, by, bgz);
    push(ax, ay, az);
    push(bx, by, bgz);
    push(ax, ay, agz);
  }
  return out;
}

export class Obstacle3dLayer implements CustomLayerInterface {
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "3d" as const;

  private gl: WebGLRenderingContext | null = null;
  private lineProgram: WebGLProgram | null = null;
  private pointProgram: WebGLProgram | null = null;
  private lineBuffer: WebGLBuffer | null = null;
  private pointBuffer: WebGLBuffer | null = null;
  private wallBuffer: WebGLBuffer | null = null;
  private coneBuffer: WebGLBuffer | null = null;
  private lineVertexCount = 0;
  private pointVertexCount = 0;
  private wallVertexCount = 0;
  private coneVertexCount = 0;

  // line program uniforms / attribs
  private lineMatrixLoc: WebGLUniformLocation | null = null;
  private lineViewportLoc: WebGLUniformLocation | null = null;
  private lineHalfWidthLoc: WebGLUniformLocation | null = null;
  private lineExaggerationLoc: WebGLUniformLocation | null = null;
  private linePosCurLoc = 0;
  private linePosOthLoc = 0;
  private lineSideLoc = 0;
  private lineColorLoc = 0;

  // join program (round joins and caps) — 7 floats per vertex, xyz then rgba
  private joinProgram: WebGLProgram | null = null;
  private joinBuffer: WebGLBuffer | null = null;
  private joinVertexCount = 0;
  private joinMatrixLoc: WebGLUniformLocation | null = null;
  private joinExaggerationLoc: WebGLUniformLocation | null = null;
  private joinHalfWidthLoc: WebGLUniformLocation | null = null;
  private joinPosLoc = 0;
  private joinColorLoc = 0;

  // point program uniforms / attribs
  private pointMatrixLoc: WebGLUniformLocation | null = null;
  private pointSizeLoc: WebGLUniformLocation | null = null;
  private pointViewportLoc: WebGLUniformLocation | null = null;
  private pointIconsLoc: WebGLUniformLocation | null = null;
  private pointLightedLoc = 0;
  private iconTexture: WebGLTexture | null = null;
  private pointExaggerationLoc: WebGLUniformLocation | null = null;
  private pointPosLoc = 0;

  // cone program — per-vertex colour; draws cable curtains and the `cones` triangle list
  private coneProgram: WebGLProgram | null = null;
  private coneMatrixLoc: WebGLUniformLocation | null = null;
  private coneExaggerationLoc: WebGLUniformLocation | null = null;
  private conePosLoc = 0;
  private coneColorLoc = 0;

  // Per-frame multiplier applied to vertex z so obstacles match the
  // exaggerated terrain underneath. Mutable so a future runtime-tunable
  // exaggeration can re-bind without rebuilding vertex buffers.
  private terrainExaggeration = 1.0;
  private obstacleMap?: MlMap;
  private obstacleMinZoom = 0;

  setObstacleMinZoom(zoom: number): void {
    this.obstacleMinZoom = zoom;
  }

  // Data is always cached on the JS side. `setData` updates the cache and
  // also uploads to GPU when `gl` is live; otherwise (onAdd hasn't run yet,
  // or onRemove has torn buffers down) the cache survives so a subsequent
  // onAdd uploads from it. Critical for MapLibre style reloads, which can
  // remove+re-add custom layers under the hood.
  private cachedLines: Float32Array = new Float32Array(0);
  private cachedGround: Float32Array = new Float32Array(0);
  private cachedPoints: Float32Array = new Float32Array(0);
  private cachedCones: Float32Array = new Float32Array(0);
  private cachedLineColors: Float32Array = new Float32Array(0);

  // Vertex data is stored relative to this mercator origin. A mercator
  // coordinate in float32 rounds to about 2 m, about 1 px at high zoom, which
  // made the cables wobble and notch at every segment join. The origin goes
  // back into the matrix in float64 each frame (see rtcMatrix).
  private originX = 0;
  private originY = 0;
  private readonly rtc = new Float32Array(16);

  constructor(
    id: string,
    terrainExaggeration: number = 1.0,
    private readonly lineWidthPx = LINE_WIDTH_PX,
    private readonly showCurtains = true
  ) {
    this.id = id;
    this.terrainExaggeration = terrainExaggeration;
  }

  setTerrainExaggeration(value: number): void {
    this.terrainExaggeration = value;
  }

  onAdd(_map: MlMap, gl: WebGLRenderingContext): void {
    this.obstacleMap = _map;
    // Idempotency guard: if onAdd fires twice without an intervening
    // onRemove (HMR / style reload), free the prior GPU resources first
    // so we don't leak.
    if (this.gl) this.releaseGpu();

    // Defer assigning `this.gl` until compile/link succeed — otherwise a
    // throw mid-setup leaves the layer with a live gl ref but null
    // program/buffer fields, and the next `setData` silently no-ops in
    // `uploadLines` (round-3 review C7).
    try {
      const lineVs = compile(gl, gl.VERTEX_SHADER, LINE_VERT_SRC);
      const lineFs = compile(gl, gl.FRAGMENT_SHADER, LINE_FRAG_SRC);
      this.lineProgram = link(gl, lineVs, lineFs);
      this.lineMatrixLoc = gl.getUniformLocation(this.lineProgram, "u_matrix");
      this.lineViewportLoc = gl.getUniformLocation(this.lineProgram, "u_viewport");
      this.lineHalfWidthLoc = gl.getUniformLocation(this.lineProgram, "u_half_width");
      this.lineExaggerationLoc = gl.getUniformLocation(this.lineProgram, "u_terrain_exaggeration");
      this.linePosCurLoc = gl.getAttribLocation(this.lineProgram, "a_pos_cur");
      this.linePosOthLoc = gl.getAttribLocation(this.lineProgram, "a_pos_oth");
      this.lineSideLoc = gl.getAttribLocation(this.lineProgram, "a_side");
      this.lineColorLoc = gl.getAttribLocation(this.lineProgram, "a_color");

      const joinVs = compile(gl, gl.VERTEX_SHADER, JOIN_VERT_SRC);
      const joinFs = compile(gl, gl.FRAGMENT_SHADER, JOIN_FRAG_SRC);
      this.joinProgram = link(gl, joinVs, joinFs);
      this.joinMatrixLoc = gl.getUniformLocation(this.joinProgram, "u_matrix");
      this.joinExaggerationLoc = gl.getUniformLocation(this.joinProgram, "u_terrain_exaggeration");
      this.joinHalfWidthLoc = gl.getUniformLocation(this.joinProgram, "u_half_width");
      this.joinPosLoc = gl.getAttribLocation(this.joinProgram, "a_pos");
      this.joinColorLoc = gl.getAttribLocation(this.joinProgram, "a_color");

      const pointVs = compile(gl, gl.VERTEX_SHADER, POINT_VERT_SRC);
      const pointFs = compile(gl, gl.FRAGMENT_SHADER, POINT_FRAG_SRC);
      this.pointProgram = link(gl, pointVs, pointFs);
      this.pointMatrixLoc = gl.getUniformLocation(this.pointProgram, "u_matrix");
      this.pointSizeLoc = gl.getUniformLocation(this.pointProgram, "u_point_size");
      this.pointExaggerationLoc = gl.getUniformLocation(this.pointProgram, "u_terrain_exaggeration");
      this.pointPosLoc = gl.getAttribLocation(this.pointProgram, "a_mercator");
      this.pointViewportLoc = gl.getUniformLocation(this.pointProgram, "u_viewport");
      this.pointIconsLoc = gl.getUniformLocation(this.pointProgram, "u_icons");
      this.pointLightedLoc = gl.getAttribLocation(this.pointProgram, "a_lighted");
      this.iconTexture = this.createIconTexture(gl);

      const coneVs = compile(gl, gl.VERTEX_SHADER, CONE_VERT_SRC);
      const coneFs = compile(gl, gl.FRAGMENT_SHADER, CONE_FRAG_SRC);
      this.coneProgram = link(gl, coneVs, coneFs);
      this.coneMatrixLoc = gl.getUniformLocation(this.coneProgram, "u_matrix");
      this.coneExaggerationLoc = gl.getUniformLocation(this.coneProgram, "u_terrain_exaggeration");
      this.conePosLoc = gl.getAttribLocation(this.coneProgram, "a_pos");
      this.coneColorLoc = gl.getAttribLocation(this.coneProgram, "a_color");

      this.lineBuffer = gl.createBuffer();
      this.joinBuffer = gl.createBuffer();
      this.pointBuffer = gl.createBuffer();
      this.wallBuffer = gl.createBuffer();
      this.coneBuffer = gl.createBuffer();
    } catch (err) {
      // Tear down anything we did create so we don't leak; leave the
      // layer in the clean post-construct state so a future re-add gets
      // another try.
      this.gl = gl;
      this.releaseGpu();
      this.gl = null;
      throw err;
    }

    this.gl = gl;

    // Re-upload cached data — supports both 'setData arrived before onAdd'
    // (early path) and 'remove → re-add same instance' (style reload).
    if (this.cachedLines.length > 0) this.uploadLines(this.cachedLines);
    if (this.cachedPoints.length > 0) this.uploadPoints(this.cachedPoints);
    if (this.cachedCones.length > 0) this.uploadCones(this.cachedCones);
  }

  // MapLibre caches texture and pixel-store state, so every value this changes
  // goes back to what it was.
  private createIconTexture(gl: WebGLRenderingContext): WebGLTexture | null {
    const tex = gl.createTexture();
    if (!tex) return null;
    const prevActive = gl.getParameter(gl.ACTIVE_TEXTURE) as number;
    gl.activeTexture(gl.TEXTURE0);
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null;
    const prevPremultiply = gl.getParameter(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL) as boolean;
    const prevFlip = gl.getParameter(gl.UNPACK_FLIP_Y_WEBGL) as boolean;
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, buildIconAtlas());
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, prevPremultiply);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, prevFlip);
    gl.activeTexture(prevActive);
    return tex;
  }

  private releaseGpu(): void {
    const gl = this.gl;
    if (!gl) return;
    if (this.lineProgram) gl.deleteProgram(this.lineProgram);
    if (this.joinProgram) gl.deleteProgram(this.joinProgram);
    if (this.joinBuffer) gl.deleteBuffer(this.joinBuffer);
    if (this.pointProgram) gl.deleteProgram(this.pointProgram);
    if (this.coneProgram) gl.deleteProgram(this.coneProgram);
    if (this.lineBuffer) gl.deleteBuffer(this.lineBuffer);
    if (this.pointBuffer) gl.deleteBuffer(this.pointBuffer);
    if (this.wallBuffer) gl.deleteBuffer(this.wallBuffer);
    if (this.coneBuffer) gl.deleteBuffer(this.coneBuffer);
    if (this.iconTexture) gl.deleteTexture(this.iconTexture);
    this.iconTexture = null;
    this.lineProgram = null;
    this.joinProgram = null;
    this.joinBuffer = null;
    this.joinVertexCount = 0;
    this.pointProgram = null;
    this.coneProgram = null;
    this.lineBuffer = null;
    this.pointBuffer = null;
    this.wallBuffer = null;
    this.coneBuffer = null;
    this.lineVertexCount = 0;
    this.pointVertexCount = 0;
    this.wallVertexCount = 0;
    this.coneVertexCount = 0;
  }

  onRemove(): void {
    this.releaseGpu();
    this.gl = null;
    // NOTE: cached* are deliberately preserved so a re-add (style reload)
    // can re-upload the last-known geometry.
  }

  private uploadLines(data: Float32Array): void {
    const gl = this.gl;
    if (!gl || !this.lineBuffer) return;
    const expanded = expandLinesToQuads(data, this.cachedLineColors);
    if (this.joinBuffer) {
      const joins = lineJoins(data, this.cachedLineColors);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.joinBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, joins, gl.STATIC_DRAW);
      this.joinVertexCount = joins.length / 7;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, expanded, gl.STATIC_DRAW);
    this.lineVertexCount = expanded.length / LINE_STRIDE;

    // The curtain shares the line input (top edge) plus the cached ground
    // elevations (bottom edge). Built here so it stays in lockstep with the
    // line buffer on every upload.
    if (this.wallBuffer && this.showCurtains) {
      const walls = expandLinesToWalls(data, this.cachedGround, this.cachedLineColors);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.wallBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, walls, gl.STATIC_DRAW);
      this.wallVertexCount = walls.length / 7;
    }
  }

  private uploadPoints(data: Float32Array): void {
    const gl = this.gl;
    if (!gl || !this.pointBuffer) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.pointBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    this.pointVertexCount = data.length / 4;
  }

  // Cone fill is a ready triangle list, 7 floats per vertex (x,y,z, r,g,b,a) —
  // position + per-vertex colour. No expansion.
  private uploadCones(data: Float32Array): void {
    const gl = this.gl;
    if (!gl || !this.coneBuffer) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.coneBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    this.coneVertexCount = data.length / 7;
  }

  /**
   * Mercator inputs are float64 on purpose: the origin is subtracted before
   * the float32 upload (see `originX`). `lineColors` is RGBA per cable segment.
   */
  setData(
    lines: Float64Array,
    points: Float64Array,
    ground: Float64Array = new Float64Array(0),
    cones: Float64Array = new Float64Array(0),
    lineColors: Float32Array = new Float32Array(0)
  ): void {
    // Defensive copy so the cache can't be mutated under us by the caller
    // (round-3 review C12). The callers pass fresh `Float64Array` positions and
    // a fresh `Float32Array` of colours; if that ever changes to a pooled buffer the cache
    // would otherwise alias to a mutable arena. `ground` carries the per-vertex
    // terrain elevation for the curtain's bottom edge (same layout as `lines`);
    // it must be cached before uploadLines, which reads it to build the wall.
    // `cones` is a ready triangle list, 7 floats per vertex (x,y,z, r,g,b,a).
    // The obstacle refresh passes none, because webcams are hidden in 3D. The
    // approach-sector layer passes its sector fills.
    const first = lines.length > 0 ? lines : points.length > 0 ? points : cones;
    this.originX = first.length > 0 ? first[0] : 0;
    this.originY = first.length > 0 ? first[1] : 0;
    this.cachedLines = this.relativeToOrigin(lines, 3);
    this.cachedGround = this.relativeToOrigin(ground, 3);
    this.cachedPoints = this.relativeToOrigin(points, 4);
    this.cachedCones = this.relativeToOrigin(cones, 7);
    // Missing or mismatched colours draw opaque white rather than nothing.
    this.cachedLineColors =
      lineColors.length * 6 === lines.length * 4
        ? new Float32Array(lineColors)
        : new Float32Array((lines.length / 6) * 4).fill(1);
    if (!this.gl) return;
    this.uploadLines(this.cachedLines);
    this.uploadPoints(this.cachedPoints);
    this.uploadCones(this.cachedCones);
  }

  /** Copy with x and y moved to the origin; `stride` floats per vertex, xyz first. */
  private relativeToOrigin(data: Float64Array, stride: number): Float32Array {
    const out = new Float32Array(data);
    for (let i = 0; i + 1 < data.length; i += stride) {
      out[i] = data[i] - this.originX;
      out[i + 1] = data[i + 1] - this.originY;
    }
    return out;
  }

  /** `matrix` times a translation to the origin, computed in float64. */
  private rtcMatrix(matrix: ArrayLike<number>): Float32Array {
    for (let i = 0; i < 12; i++) this.rtc[i] = matrix[i];
    for (let r = 0; r < 4; r++) {
      this.rtc[12 + r] = matrix[r] * this.originX + matrix[4 + r] * this.originY + matrix[12 + r];
    }
    return this.rtc;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, options: CustomRenderMethodInput): void {
    // Nothing to draw → skip all GL calls for an empty buffer (round-3 C9).
    if (this.lineVertexCount === 0 && this.pointVertexCount === 0 && this.coneVertexCount === 0) return;
    if (!this.lineProgram && !this.pointProgram && !this.coneProgram) return;

    // `defaultProjectionData.mainMatrix` is the matrix the official MapLibre
    // 3D-model-on-terrain custom-layer example uses; it correctly handles
    // depth at all zooms under terrain. `modelViewProjectionMatrix` diverges
    // from it at high zoom with terrain enabled, causing obstacles to clip
    // out of the depth range.
    const matrix = this.rtcMatrix(options.defaultProjectionData.mainMatrix as unknown as ArrayLike<number>);
    const showObstacles = (this.obstacleMap?.getZoom() ?? 0) >= this.obstacleMinZoom;

    // No save and restore of GL state: MapLibre's drawCustom calls
    // context.setDirty() after render(), which covers depth, blend, cull,
    // program, texture and buffer bindings, and sets them again before its
    // next draw. Each getParameter or isEnabled is a synchronous GPU round
    // trip (an IPC in Firefox and WebKit), and this runs on every frame.

    // Depth baseline. `renderingMode: '3d'` keeps the depth buffer (terrain
    // already wrote into it). MapLibre's 2D paint passes commonly disable
    // depth writes, so we set explicitly. Every pass below then turns the depth
    // test off, so towers, curtains and cables always read on top of the terrain.
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.depthFunc(gl.LEQUAL);

    // Logical-pixel widths. drawingBuffer dims are device px; the screen-
    // space extrusion math runs in viewport space (NDC * 0.5 * viewport
    // gives px), so we scale half-width by DPR to keep CSS-px constancy.
    const dpr = typeof window !== "undefined" ? Math.max(1, window.devicePixelRatio || 1) : 1;

    // Pass 1 — point obstacles (towers): icon sprites with the depth test OFF,
    // like the cables. The smoothed terrain mesh can sit more than 25 m above a
    // valley floor, and a depth-tested icon there vanished while its 2D icon
    // stayed suppressed. Points are 4 floats: x, y, z, lighted.
    if (showObstacles && this.pointVertexCount > 0 && this.pointProgram && this.pointBuffer && this.iconTexture) {
      gl.disable(gl.DEPTH_TEST);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.iconTexture);
      gl.useProgram(this.pointProgram);
      gl.uniformMatrix4fv(this.pointMatrixLoc, false, matrix);
      gl.uniform1f(this.pointSizeLoc, POINT_SIZE_PX * dpr);
      gl.uniform2f(this.pointViewportLoc, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform1i(this.pointIconsLoc, 0);
      gl.uniform1f(this.pointExaggerationLoc, this.terrainExaggeration);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.pointBuffer);
      gl.enableVertexAttribArray(this.pointPosLoc);
      gl.vertexAttribPointer(this.pointPosLoc, 3, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(this.pointLightedLoc);
      gl.vertexAttribPointer(this.pointLightedLoc, 1, gl.FLOAT, false, 16, 12);
      gl.drawArrays(gl.POINTS, 0, this.pointVertexCount);
      gl.disableVertexAttribArray(this.pointPosLoc);
      gl.disableVertexAttribArray(this.pointLightedLoc);
    }

    // Passes 2 + 3 — the two translucent overlays (cable curtains, then the
    // `cones` triangle list) share the SAME blend/cull/depth config, set once for both passes.
    // Both passes: depth test OFF, alpha-blended, double-sided (cull off).
    // Blend writes RGB only (alpha ZERO/ONE) so it doesn't punch holes in a
    // premultiplied canvas. Depth test is restored by the common tail.
    //
    // Depth OFF rationale — curtain: a depth-tested curtain seams against the
    // terrain surface where `curtain depth ≈ terrain depth`, and our PMTiles DEM
    // samples never match MapLibre's terrain mesh to the bit, so that seam
    // z-fights and the sheet grows/shrinks as terrain LOD morphs. With no depth
    // comparison there is no seam to fight; the terrain-following bottom edge
    // (dense-sampled in refreshObstacles3d) keeps the sheet pinned near the
    // ground. The triangle list: depth off so it always reads on top — terrain
    // never occludes it.
    const drawWalls = showObstacles && this.wallVertexCount > 0 && this.coneProgram && this.wallBuffer;
    const drawCones = this.coneVertexCount > 0 && this.coneProgram && this.coneBuffer;
    if (drawWalls || drawCones) {
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ZERO, gl.ONE);
      gl.disable(gl.CULL_FACE);
      gl.disable(gl.DEPTH_TEST);

      // Pass 2 — cable curtains, then pass 3 — the `cones` triangle list. Both use the
      // cone program: 7 floats per vertex, xyz then rgba.
      gl.useProgram(this.coneProgram);
      gl.uniformMatrix4fv(this.coneMatrixLoc, false, matrix);
      gl.uniform1f(this.coneExaggerationLoc, this.terrainExaggeration);
      gl.enableVertexAttribArray(this.conePosLoc);
      gl.enableVertexAttribArray(this.coneColorLoc);
      const draws: [WebGLBuffer | null, number][] = [
        [drawWalls ? this.wallBuffer : null, this.wallVertexCount],
        [drawCones ? this.coneBuffer : null, this.coneVertexCount],
      ];
      for (const [buffer, count] of draws) {
        if (!buffer) continue;
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.vertexAttribPointer(this.conePosLoc, 3, gl.FLOAT, false, 28, 0);
        gl.vertexAttribPointer(this.coneColorLoc, 4, gl.FLOAT, false, 28, 12);
        gl.drawArrays(gl.TRIANGLES, 0, count);
      }
      gl.disableVertexAttribArray(this.conePosLoc);
      gl.disableVertexAttribArray(this.coneColorLoc);
    }

    // Pass 4 — cable lines, drawn LAST with the depth test DISABLED so the
    // cable is never obscured by terrain (or its own curtain) and always reads
    // on top. Screen-space extruded quads — see LINE_VERT_SRC.
    if (showObstacles && this.lineVertexCount > 0 && this.lineProgram && this.lineBuffer) {
      gl.disable(gl.DEPTH_TEST);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.useProgram(this.lineProgram);
      gl.uniformMatrix4fv(this.lineMatrixLoc, false, matrix);
      gl.uniform2f(this.lineViewportLoc, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.uniform1f(this.lineHalfWidthLoc, this.lineWidthPx * 0.5 * dpr);
      gl.uniform1f(this.lineExaggerationLoc, this.terrainExaggeration);

      gl.bindBuffer(gl.ARRAY_BUFFER, this.lineBuffer);
      const stride = LINE_STRIDE * 4;
      gl.enableVertexAttribArray(this.linePosCurLoc);
      gl.vertexAttribPointer(this.linePosCurLoc, 3, gl.FLOAT, false, stride, 0);
      gl.enableVertexAttribArray(this.linePosOthLoc);
      gl.vertexAttribPointer(this.linePosOthLoc, 3, gl.FLOAT, false, stride, 3 * 4);
      gl.enableVertexAttribArray(this.lineSideLoc);
      gl.vertexAttribPointer(this.lineSideLoc, 1, gl.FLOAT, false, stride, 6 * 4);
      gl.enableVertexAttribArray(this.lineColorLoc);
      gl.vertexAttribPointer(this.lineColorLoc, 4, gl.FLOAT, false, stride, 7 * 4);
      gl.drawArrays(gl.TRIANGLES, 0, this.lineVertexCount);
      gl.disableVertexAttribArray(this.lineColorLoc);
      gl.disableVertexAttribArray(this.linePosCurLoc);
      gl.disableVertexAttribArray(this.linePosOthLoc);
      gl.disableVertexAttribArray(this.lineSideLoc);

      if (this.joinVertexCount > 0 && this.joinProgram && this.joinBuffer) {
        gl.useProgram(this.joinProgram);
        gl.uniformMatrix4fv(this.joinMatrixLoc, false, matrix);
        gl.uniform1f(this.joinExaggerationLoc, this.terrainExaggeration);
        gl.uniform1f(this.joinHalfWidthLoc, this.lineWidthPx * 0.5 * dpr);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.joinBuffer);
        gl.enableVertexAttribArray(this.joinPosLoc);
        gl.vertexAttribPointer(this.joinPosLoc, 3, gl.FLOAT, false, 28, 0);
        gl.enableVertexAttribArray(this.joinColorLoc);
        gl.vertexAttribPointer(this.joinColorLoc, 4, gl.FLOAT, false, 28, 12);
        gl.drawArrays(gl.POINTS, 0, this.joinVertexCount);
        gl.disableVertexAttribArray(this.joinPosLoc);
        gl.disableVertexAttribArray(this.joinColorLoc);
      }
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }
}

/**
 * Convert (lng, lat, elev_metres) to MapLibre mercator coordinates for upload
 * as vertex data. The mercator matrix passed to `render()` already accounts
 * for the appropriate altitude scaling.
 */
export function lngLatElevToMercator(lng: number, lat: number, elev: number): [number, number, number] {
  const m = MercatorCoordinate.fromLngLat({ lng, lat }, elev);
  return [m.x, m.y, m.z];
}
